import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchDashboardStats } from '../reducers/dashboardSlice';
import { fetchForms } from '../reducers/formsSlice';
import { fetchSubmissions } from '../reducers/submissionsSlice';
import { useSync } from './useSync';
import { storageService } from '../services/storageService';
import { toastService } from '../services/toastService';

export const useDashboard = (options = {}) => {
  const { onUnauthorizedClick } = options;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { stats, status: statsStatus } = useSelector(state => state.dashboard);
  const { items: submissions, status: submissionsStatus, error: submissionsError } = useSelector(state => state.submissions);
  const { forms, status: formsStatus } = useSelector(state => state.forms);

  const isUnauthorized = useMemo(() => {
    if (submissionsStatus !== 'failed' || !submissionsError) return false;
    const err = submissionsError.toLowerCase();
    return err.includes('403') || err.includes('401') || err.includes('permission') || err.includes('authorized');
  }, [submissionsStatus, submissionsError]);
  const { pendingCount, lastSyncTime } = useSync();
  const [storageInfo, setStorageInfo] = useState({ usedPercentage: 0, availableMB: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchDashboardStats(true));
    dispatch(fetchSubmissions({ page: 1, limit: 100, forceRefresh: true }));
    dispatch(fetchForms(true));

    storageService
      .getStorageInfo()
      .then(info => setStorageInfo(info))
      .catch(() => {});
  }, [dispatch]);

  const handleSubmissionsClick = useCallback(() => {
    if (!navigator.onLine) {
      toastService.warning('Submissions cannot be viewed in offline mode.');
      return;
    }

    if (isUnauthorized) {
      // Re-fetch to check if permissions were restored
      dispatch(fetchSubmissions({ page: 1, limit: 100, forceRefresh: true }))
        .then(result => {
          // If the action was fulfilled (succeeded), navigate
          if (result && !result.error) {
            navigate('/forms/data');
          } else {
            onUnauthorizedClick?.();
          }
        })
        .catch(() => {
          onUnauthorizedClick?.();
        });
      return;
    }

    navigate('/forms/data');
  }, [navigate, isUnauthorized, onUnauthorizedClick, dispatch]);

  const statsCardsConfig = useMemo(() => {
    const totalSubmissions = stats.total_submissions;
    const syncedSubmissions = Math.max(0, totalSubmissions - pendingCount);
    const syncStatusPercentage = totalSubmissions > 0 ? (syncedSubmissions / totalSubmissions) * 100 : 100;
    const responseRate = Math.min(stats.response_rate || 0, 100);
    const lastSync = lastSyncTime ? new Date(lastSyncTime).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never';
    const formatStorage = mb => (mb >= 1000 ? `${(mb / 1000).toFixed(1)} GB Free` : `${mb.toFixed(1)} MB Free`);

    return [
      {
        id: 'assigned-projects',
        title: 'Forms',
        value: stats.total_assigned_forms,
        change: `${responseRate}% response rate`,
        icon: 'completed',
        path: '/forms'
      },
      {
        id: 'total-submissions',
        title: 'Submissions',
        value: totalSubmissions,
        change: `Today: ${stats.submissions_today}`,
        icon: 'pending',
        onClick: handleSubmissionsClick
      },
      {
        id: 'sync-status',
        title: 'Sync Status',
        value: `${syncStatusPercentage.toFixed(0)}%`,
        change: `Last Sync: ${lastSync}`,
        icon: 'sync',
        path: '/sync'
      },
      {
        id: 'storage-used',
        title: 'Storage',
        value: `${storageInfo.usedPercentage.toFixed(1)}%`,
        change: formatStorage(storageInfo.availableMB),
        icon: 'storage',
        path: '/settings'
      }
    ];
  }, [stats, pendingCount, lastSyncTime, storageInfo, handleSubmissionsClick]);

  const isStatsLoading = statsStatus === 'loading' || statsStatus === 'idle';
  const isSubmissionsLoading = submissionsStatus === 'loading';
  const isFormsLoading = formsStatus === 'loading' || formsStatus === 'idle';

  // Only show the "macro" skeleton if stats are completely missing and loading
  const isLoading = isStatsLoading && Object.keys(stats).length === 0;

  return {
    stats,
    submissions,
    forms,
    isModalOpen,
    setIsModalOpen,
    statsCardsConfig,
    isLoading,
    isStatsLoading,
    isSubmissionsLoading,
    isFormsLoading,
    isUnauthorized
  };
};
